import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { Certificate } from '@adornis/buchungsheld-db/entities/certificate.js';
import { CertificateAttachedProduct } from '@adornis/buchungsheld-db/entities/products.js';
import type { TextMultipleLangItem } from '@adornis/buchungsheld-db/entities/TextMultipleLangItem.js';
import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import { FormField } from '@adornis/formfield/form-field.js';
import '@adornis/popover/x-dropdown-selection.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, map, of, switchMap } from 'rxjs';
import './bh-admin-multi-lang-text';

/**
 * @element bh-product-certificate-picker
 */
@customElement('bh-product-certificate-picker')
export class BHProductCertifcatePicker extends FormField<CertificateAttachedProduct> {
  @state() readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  @property({ attribute: 'institution-id' }) institutionID = new RXController<string | null>(this, null);

  @state() private readonly _allCertificates = new RXController(
    this,
    this.institutionID.observable.pipe(
      switchMap(institutionID =>
        institutionID
          ? Certificate.getCertificatesByInstitution(institutionID)({ _class: 1, text: 1, title: 1, fileID: 1 })
          : of(null),
      ),
    ),
  );

  @state() protected _selectedCertificate = new RXController(
    this,
    combineLatest([this._allCertificates.observable, this.value.observable]).pipe(
      map(([allCert, attachedCert]) => (allCert ?? []).find(cert => cert._id === attachedCert?.certificateID)),
    ),
  );

  protected async _applyCertificate(targetCert: Maybe<Certificate>) {
    const attVal = this.value.value;
    let nextAttach = attVal;

    if (attVal) {
      nextAttach = attVal;
      if (!(await TrDialog.confirm('Möchten Sie die Zertifikats-Beschreibungen beibehalten?'))) {
        nextAttach.descriptionsHTML = [];
      }
      nextAttach.certificateID = null;
    } else nextAttach = new CertificateAttachedProduct({});

    if (targetCert) nextAttach.certificateID = targetCert._id;

    this.value.next(nextAttach);
    this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: nextAttach } }));
    this.requestUpdate();
  }

  protected _renderCertificateItem(curCert: Certificate) {
    return html`
      <tr-flex
        horizontal
        space="xs"
        crossaxis-center
        ${css({ fontSize: this.spacing.md, padding: `${this.spacing.xxs} 0` })}
      >
        <tr-icon> history_edu </tr-icon>
        <tr-text> ${curCert.title} </tr-text>
      </tr-flex>
    `;
  }

  protected _updateDescriptions(nextDesc: TextMultipleLangItem[]) {
    const curVal = this.value.value ?? new CertificateAttachedProduct({});
    curVal.descriptionsHTML = nextDesc;
    this.value.next(curVal);
  }

  protected _renderDescriptions() {
    if (!this.value.value) return nothing;

    return html`
      <bh-admin-multi-lang-text
        label="${this._translation.translate('field', 'description')}*"
        .value=${this.value.value.descriptionsHTML}
        .buildifyConfigs=${{ richText: false, listings: true }}
        @value-picked=${(valEvt: ValueEvent<TextMultipleLangItem[]>) => this._updateDescriptions(valEvt.detail.value)}
        no-formatting
      ></bh-admin-multi-lang-text>
    `;
  }

  override render() {
    if ((this._allCertificates.value ?? []).length >= 1)
      return html`
        <x-basic-card>
          <x-flex padding="md" space="md">
            <x-flex horizontal space="sm" crossaxis-end>
              <tr-dropdown-selection
                .value=${this._selectedCertificate.value}
                placeholder="Workshop spezifisches Zertifikat"
                .selectables=${this._allCertificates.value}
                .renderString=${(curCert: Certificate) => this._renderCertificateItem(curCert)}
                @value-picked="${(evt: ValueEvent<Maybe<Certificate>>) => this._applyCertificate(evt.detail.value)}"
                ${css({ width: '100%' })}
              ></tr-dropdown-selection>
              <x-button
                mode="icon"
                icon="close"
                ${css({ background: this.colors.secondary, color: this.colors.white, marginBottom: '10px' })}
                @click=${() => this.value.next(null)}
              ></x-button>
            </x-flex>

            ${this._renderDescriptions()}
          </x-flex>
        </x-basic-card>
      `;

    return html`
      <x-basic-card>
        <x-flex horizontal crossaxis-center padding="lg" space="md" space-between>
          <x-text> Noch keine Zertifikate verfügbar </x-text>
        </x-flex>
      </x-basic-card>
    `;
  }
}
